<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="個別サービス設定"
  >
    <template v-slot:body="{ cancel }">
      <validation-observer ref="observer" v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- 名前 -->
                <v-row>
                  <v-col cols="12">
                    <app-badge
                      item="名前"
                      :msg="
                        $filter.message('info_alpha_num', { maxLength: 63 })
                      "
                      placement="end"
                    />
                    <validation-provider
                      name="serviceName"
                      v-model="form.serviceName"
                      label="名前"
                      :rules="{
                        required: true,
                        is_half_width_alpha_numeric: true,
                        max: { length: 63 },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
                <!-- プロトコル -->
                <v-row>
                  <v-col cols="12">
                    <app-badge
                      item="プロトコル"
                      :is-required="false"
                      :is-info="false"
                    />
                    <v-radio-group
                      inline
                      density="comfortable"
                      v-model="form.protocol"
                      hide-details
                    >
                      <v-radio value="TCP" label="TCP" />
                      <v-radio value="UDP" label="UDP" />
                      <v-radio value="IP" label="IP" />
                      <v-radio value="ICMP" label="ICMP" />
                    </v-radio-group>
                  </v-col>
                </v-row>
                <template
                  v-if="form.protocol === 'TCP' || form.protocol === 'UDP'"
                >
                  <!-- Port -->
                  <v-row>
                    <v-col cols="12">
                      <validation-provider
                        name="portNumber"
                        v-model="form.portNumber"
                        label="Port"
                        :rules="{
                          required: true,
                          is_custom_port: true,
                          is_custom_port_length: { length: 10 },
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-badge
                          item="Port"
                          :msg="$filter.message('info_custom_port')"
                          :isRequired="true"
                          placement="end"
                        ></app-badge>
                        <app-form-input
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        />
                      </validation-provider>
                    </v-col>
                  </v-row>
                </template>
                <template v-else-if="form.protocol === 'IP'">
                  <v-row>
                    <v-col cols="12">
                      <!-- プロトコル番号 -->
                      <validation-provider
                        name="protocolNumber"
                        v-model.number="form.protocolNumber"
                        label="プロトコル番号"
                        :rules="{
                          required: true,
                          numeric: true,
                          between: { min: 0, max: 254 },
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-badge
                          item="プロトコル番号"
                          :msg="$filter.message('info_num_max', { max: 254 })"
                          :isRequired="true"
                          placement="end"
                        ></app-badge>
                        <app-form-input-number
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        />
                      </validation-provider>
                    </v-col>
                  </v-row>
                </template>
                <template v-else-if="form.protocol === 'ICMP'">
                  <v-row>
                    <v-col cols="12">
                      <!-- ICMP Type -->
                      <validation-provider
                        name="icmpType"
                        v-model.number="form.icmpType"
                        label="ICMP Type"
                        :rules="{
                          required: true,
                          numeric: true,
                          between: { min: 0, max: 255 },
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-badge
                          item="ICMP Type"
                          :msg="$filter.message('info_num_max', { max: 255 })"
                          :isRequired="true"
                          placement="end"
                        ></app-badge>
                        <app-form-input-number
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        />
                      </validation-provider>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <!-- ICMP Code -->
                      <validation-provider
                        name="icmpCode"
                        v-model.number="form.icmpCode"
                        label="ICMP Code"
                        :rules="{
                          required: true,
                          numeric: true,
                          between: { min: 0, max: 255 },
                        }"
                        v-slot="{ componentField, errorMessage, meta }"
                      >
                        <app-badge
                          item="ICMP Code"
                          :msg="$filter.message('info_num_max', { max: 255 })"
                          :isRequired="true"
                          placement="end"
                        />
                        <app-form-input-number
                          v-bind="componentField"
                          :error-messages="errorMessage"
                          :field-meta="meta"
                        />
                      </validation-provider>
                    </v-col>
                  </v-row>
                </template>
                <!-- 備考 -->
                <v-row>
                  <v-col cols="12">
                    <validation-provider
                      name="description"
                      v-model="form.description"
                      label="備考"
                      :rules="{ max: { length: 20 } }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-badge
                        item="備考"
                        :msg="
                          $filter.message('info_max_length', { maxLength: 20 })
                        "
                        :isRequired="false"
                        placement="end"
                      ></app-badge>
                      <app-form-input
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" variant="flat" color="primary">
                  {{ customServiceIntranet ? "変更" : "追加" }}
                </v-btn>
                <v-btn variant="outlined" color="primary" @click="cancel"
                  >キャンセル
                </v-btn>
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type {
  CustomServiceIntranet,
  CustomServiceIntranetfw,
} from "@/apis/CustomServiceApi";
import { VueFinalModal } from "vue-final-modal";
import AppFormInputNumber from "@/components/AppFormInputNumber.vue";
import AppFormInput from "@/components/AppFormInput.vue";

export default defineComponent({
  name: "ServiceIntranetFwEdit",
  components: { AppFormInput, AppFormInputNumber },
  props: {
    /**
     * ポリシー種別
     * INTRANET_FW ：イントラネットFW
     * INTRANET_FW_PFGW ：イントラネットFW（PFGW）
     */
    policyType: {
      type: String as PropType<"INTRANET_FW" | "INTRANET_FW_PFGW">,
      required: true,
    },
    /**
     * E番号(ACT)
     * ポリシー種別でINTRANET_FW_PFGWを指定時必須
     */
    enumberAct: {
      type: String as PropType<string>,
      default: undefined,
    },
    customServiceIntranet: {
      type: Object as PropType<CustomServiceIntranet | undefined>,
      default: undefined,
    },
    customServiceKeyId: {
      type: String as PropType<string>,
      required: true,
    },
  },
  data() {
    return {
      form: { protocol: "TCP" } as CustomServiceIntranet,
    };
  },
  async mounted() {
    // 更新であればフォームのデータを設定
    if (this.customServiceIntranet) {
      this.form = { ...this.customServiceIntranet };
    }
  },
  methods: {
    /** サービス設定(イントラネットFW)の追加・変更 */
    async submit() {
      // 確認ダイアログの表示 追加・変更でメッセージを変える
      const action = this.customServiceIntranet ? "変更" : "追加";
      await this.$confirm(`個別サービスを${action}します。よろしいですか？`);

      // プロトコルの値により使用しないプロパティのクリアを行う
      this.clearValue();

      // リクエストデータの設定
      const reqData: CustomServiceIntranetfw = {
        serviceName: this.form.serviceName,
        protocol: this.form.protocol,
        portNumber: this.form.portNumber,
        protocolNumber: this.form.protocolNumber,
        icmpType: this.form.icmpType,
        icmpCode: this.form.icmpCode,
        description: this.form.description || null,
        customServiceKeyId: this.customServiceKeyId,
        note: null,
      };

      // サービス設定(イントラネットFW)の追加・変更
      if (this.customServiceIntranet) {
        await this.$api.customServiceApi.putIntranetfwCustomService(
          this.policyType,
          this.enumberAct,
          this.form.customServiceSeq,
          reqData
        );
      } else {
        await this.$api.customServiceApi.postIntranetfwCustomService(
          this.policyType,
          this.enumberAct,
          reqData
        );
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** プロトコルの値により不要なプロパティの値をnullにする */
    clearValue() {
      if (this.form.protocol === "IP") {
        this.form.portNumber = null;
        this.form.icmpType = null;
        this.form.icmpCode = null;
      } else if (this.form.protocol === "ICMP") {
        this.form.portNumber = null;
        this.form.protocolNumber = null;
      } else {
        this.form.protocolNumber = null;
        this.form.icmpType = null;
        this.form.icmpCode = null;
      }
    },
  },
});
</script>
